import { isEqual } from "lodash";
import { useRef } from "react";
import { useMap } from "react-map-gl/maplibre";

import { useDebounceFn, useEventListener, useResizeObserver, useUnmount } from "@nens/react/hooks";
import { Extent } from "@nens/shared-utils";

import { FIT_BOUNDS_OPTIONS } from "../consts/fit-bounds-options";

// Apply given bounds to the map when they change.
// If dontApplyInitialBounds is true the initial bounds are not applied but only subsequent changes. (Used when setting the viewport somewhere else)
// Also handles the case when the map is rendered in a hidden tab and thus has no dimensions yet.
// In that case it will resize the map when the page becomes visible. Which will also trigger the resize event on the map and apply the bounds.
// Also handles the case when the map container is mid-animation (e.g. an unfolding transition).
// Calling fitBounds while the container is still resizing causes MapLibre to compute the viewport from
// the wrong dimensions, resulting in a shifted/padded view once the animation finishes.
// A ResizeObserver watches the container and only calls fitBounds once the size has stabilised.

const RESIZE_DEBOUNCE_MS = 150;

export function useMapBounds(bounds?: Extent | null, dontApplyInitialBounds?: boolean) {
  const map = useMap().current;
  const previousBoundsRef = useRef<Extent | null>(null);

  const boundsShouldBePending = document.hidden || !isEqual(previousBoundsRef.current, bounds);

  const hasPendingBoundsRef = useRef<boolean>(!dontApplyInitialBounds && boundsShouldBePending);
  // Handle bounds changes
  if (!bounds) {
    // No bounds provided, reset previous bounds and pending status
    previousBoundsRef.current = null;
    hasPendingBoundsRef.current = false;
  } else if (dontApplyInitialBounds && !previousBoundsRef.current) {
    // If dontApplyInitialBounds is true and no previous bounds, just set
    previousBoundsRef.current = bounds;
  } else if (boundsShouldBePending) {
    // If the bounds exist and have changed, mark as pending — the ResizeObserver will
    // apply them once the container has finished any ongoing resize/animation.
    previousBoundsRef.current = bounds;
    hasPendingBoundsRef.current = true;
  }

  const applyBoundsIfReady = useDebounceFn(() => {
    if (
      !map ||
      !bounds ||
      document.hidden ||
      !hasPendingBoundsRef.current ||
      map.isStyleLoaded() === undefined
    )
      return;
    const container = map.getContainer();

    // Check if map container has dimensions (doesn't have any dimensions when rendered when tab is not current)
    // And check if the map style is loaded, if not we can't apply the bounds yet and should reset the timeout
    if (!map.isStyleLoaded() || container.offsetWidth === 0 || container.offsetHeight === 0) {
      applyBoundsIfReady.run();
      return;
    }

    hasPendingBoundsRef.current = false;
    map.fitBounds(bounds, { ...FIT_BOUNDS_OPTIONS, animate: true, duration: 2000 });
  }, RESIZE_DEBOUNCE_MS);

  // This little bit is important for when someone has for example run a process in a different tab and then comes back when its done.
  // The map will have been rendered in a hidden tab and thus have incorrect dimensions. (Usually a small square of 100x100px)
  // When the user comes back to the tab, we need to trigger a resize on the map to ensure it has the correct dimensions and then apply the bounds.
  const handleVisibilityChange = () => {
    if (!document.hidden && map) {
      map.resize();
      applyBoundsIfReady.flush();
    }
  };

  useEventListener("visibilitychange", handleVisibilityChange, document);
  useResizeObserver(applyBoundsIfReady.run, map?.getContainer());
  useUnmount(applyBoundsIfReady.cancel);
  applyBoundsIfReady.run();

  return null;
}
